import { withDemoContacts } from './demo-contacts';
import { type Snapshot, type Command, parseSnapshot } from './models';
import { applyCommand } from './commands';
import { CURRENT_USER, seedSnapshot, newFounderSnapshot } from './seed';
import { profileSchema } from './models';
export interface RecordStorage {
  load(): Promise<Snapshot | null>;
  save(snapshot: Snapshot): Promise<void>;
}
export interface AppRepository {
  read(): Promise<Snapshot>;
  execute(command: Command): Promise<Snapshot>;
  reset(): Promise<Snapshot>;
  mode: 'local' | 'server';
}
export interface CalendarService {
  list(ventureId: string): Promise<Snapshot['events']>;
}
export interface ChatService {
  list(channel: string): Promise<Snapshot['messages']>;
  send(channel: string, text: string): Promise<void>;
}
export class LocalRepository implements AppRepository {
  readonly mode = 'local' as const;
  private queue: Promise<unknown> = Promise.resolve();
  constructor(private storage: RecordStorage, private getApiKey?: () => (string | undefined | Promise<string | null>)) {}
  async startProfile(input: unknown) {
    await this.queue;
    const next = newFounderSnapshot(profileSchema.parse(input));
    await this.storage.save(next);
    return next;
  }
  async read() {
    const saved = await this.storage.load();
    if (saved) {
      const parsed = parseSnapshot(saved);
      const upgraded = withDemoContacts(parsed);
      if (upgraded !== parsed) await this.storage.save(upgraded);
      return upgraded;
    }
    const seed = seedSnapshot();
    await this.storage.save(seed);
    return seed;
  }
  execute(command: Command): Promise<Snapshot> {
    const operation = this.queue.then(async () => {
      let next = applyCommand(await this.read(), command, CURRENT_USER);
      
      const isAIBot = command.type === 'message' && (command.channel.includes('venturebot-') || command.channel.includes('boardroom-')) && CURRENT_USER !== 'venture-bot';
      if (isAIBot) {
        const vId = command.channel.replace('venturebot-', '').replace('boardroom-', '');
        const v = next.ventures.find((v) => v.id === vId);
        if (v) {
          if (!v.credits) v.credits = { balance: 1000, purchaserId: CURRENT_USER, isShared: true };
          
          if (v.credits.balance <= 0 || (v.credits.purchaserId !== CURRENT_USER && !v.credits.isShared)) {
            next = applyCommand(next, { type: 'message', channel: command.channel, text: "There are no more credits. Top up if you wish to use the AI, or if you have any more inquiries that you need answered." }, 'venture-bot');
            await this.storage.save(next);
            return next;
          }
          
          v.credits.balance -= 50; // Deduct roughly 50 words per request
        }

        const history = next.messages
           .filter(m => m.channel === command.channel)
           .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

                const systemPrompt = `You are VentureBot, an expert startup advisor, CEO assistant, and strategist inside the VentureRoom app.
Your job is to give high-level strategic opinions, tips, and advice on building a startup. 
You are a "sayer", not a "doer". 

CRITICAL RULE 1: If the user asks you to write code, generate a website, design a logo, or execute a specific task (EXCEPT creating a master plan), you MUST politely decline and explicitly instruct them to "look through the Explore page to find and hire the right specialized person for the job". 
CRITICAL RULE 2: Never use the generate_website tool or any other execution tools. You only give advice.
CRITICAL RULE 3: Pivot unrelated conversations back to startups. Do not break character.
CRITICAL RULE 4 (MASTER PLAN): You HAVE explicit permission to create a "master plan" if the user asks for one. This overrides Rule 1. 
CRITICAL RULE 5: When generating a master plan, your advice MUST be ultra-specific and immediately actionable. 
- BAD (Generic): "Conduct market research", "Form an LLC", "Define core functionalities", "Build a marketing strategy".
- GOOD (Specific): "Scrape LinkedIn using Phantombuster for VPs of Sales and run a 3-step cold email sequence targeting their pain point of low conversion rates." "Build the MVP using Next.js, Tailwind, and Supabase for real-time lead tracking."
If you give generic advice like "establish a legal framework" or "identify target customers", you have failed. Tailor every single sentence specifically to the user's exact business idea.
Follow these steps exactly:
1. If the user asks for a master plan but has NOT specified what the business or idea is about, you MUST NOT generate the plan yet. Instead, briefly explain that you will assemble a council of specialized AI advisors to create it, and end your message EXACTLY with: "What exactly would you like the master plan to be about?"
2. Once the user provides the topic/idea for the master plan, you MUST generate the full detailed plan acting as a council of specialized AI advisors (e.g., Legal AI, Website AI, Coding AI, Marketing AI, Financial AI, etc.). Each paragraph or section MUST be written by a different specialized AI personality, clearly starting with their title (e.g., "**Legal AI:** ..."). Do not just list the roles; actually write out the detailed, actionable plan for the user's specific business idea from the perspective of each AI.`;
const messages = [
          { role: 'system', content: systemPrompt },
          ...history.map(m => ({
            role: m.author === 'venture-bot' ? 'assistant' as const : 'user' as const,
            content: m.text
          }))
        ];

        const tools: Record<string, unknown>[] = [];

        try {
          // DO NOT COMMIT: Hardcoded for quick local demo as requested

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const envKey = typeof import.meta !== 'undefined' ? import.meta.env?.VITE_OPENAI_API_KEY : undefined;
          const userKey = this.getApiKey ? await this.getApiKey() : undefined;
          const apiKey = userKey || envKey || 'sk-proj-YOUR_NEW_API_KEY_HERE';
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 60000);
          
          let response;
          try {
            response = await fetch('https://api.openai.com/v1/chat/completions', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apiKey}`
              },
              body: JSON.stringify({
                model: 'gpt-4o',
                messages: messages,
                tools: tools,
                max_tokens: 4096,
              }),
              signal: controller.signal
            });
          } finally {
            clearTimeout(timeout);
          }

          if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(`OpenAI Error (${response.status}): ${err.error?.message || response.statusText}`); }
          const data = await response.json();
          const message = data.choices[0].message;

          if (message.tool_calls && message.tool_calls.length > 0) {
            const toolCall = message.tool_calls[0];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let args: any = {};
            try {
              args = JSON.parse(toolCall.function.arguments);
            } catch (e) {
              throw new Error("The AI failed to finish writing the code (it got cut off). Please try asking for something simpler or say 'continue'.", { cause: e });
            }
            if (toolCall.function.name === 'generate_image') {
              
              
              const res = await fetch('https://api.openai.com/v1/images/generations', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${apiKey}`
                },
                body: JSON.stringify({
                  model: "chatgpt-image-latest",
                  prompt: args.prompt.substring(0, 1000), // Ensure it's not too long
                  n: 1,
                  size: "1024x1024"
                })
              });
              const imgData = await res.json();
              const imgObj = imgData.data?.[0];
              if (imgObj && (imgObj.url || imgObj.b64_json)) {
                const imageUrl = imgObj.url || `data:image/png;base64,${imgObj.b64_json}`;
                next = applyCommand(next, { type: 'message', channel: command.channel, text: "Here is the logo I created for you!", imageUrl: imageUrl }, 'venture-bot');
              } else {
                const errorMsg = imgData.error?.message || JSON.stringify(imgData);
                next = applyCommand(next, { type: 'message', channel: command.channel, text: `Sorry, the image generator returned an error: ${errorMsg}`.substring(0, 1900) }, 'venture-bot');
              }
            } else if (toolCall.function.name === 'generate_website') {
              next = applyCommand(next, {
                type: 'message',
                channel: command.channel,
                text: 'Here is the live website preview I built for you:',
                websiteCode: {
                  html: args.html || '',
                  css: args.css || '',
                  js: args.js || ''
                }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any, 'venture-bot');
            } else {
              next = applyCommand(next, { type: 'message', channel: command.channel, text: "Sorry, I tried to use an invalid tool." }, 'venture-bot');
            }
          } else {
            next = applyCommand(next, { type: 'message', channel: command.channel, text: String(message.content || '').substring(0, 1900) }, 'venture-bot');
          }
        } catch (e: unknown) {
          const errMsg = e instanceof Error ? e.message : 'Unknown Error';
          next = applyCommand(next, { type: 'message', channel: command.channel, text: `Sorry, I couldn't reach my OpenAI brain! Reason: ${errMsg}. Please check your API key.`.substring(0, 1900) }, 'venture-bot');
        }
      }

      await this.storage.save(next);
      return next;
    });
    this.queue = operation.catch(() => undefined);
    return operation;
  }
  async reset() {
    await this.queue;
    const seed = seedSnapshot();
    await this.storage.save(seed);
    return seed;
  }
}
export class HttpRepository implements AppRepository {
  readonly mode = 'server' as const;
  constructor(
    private url: string,
    private token: string,
  ) {
    const u = new URL(url);
    if (
      u.protocol !== 'https:' &&
      !(u.protocol === 'http:' && ['127.0.0.1', 'localhost', '10.0.2.2'].includes(u.hostname))
    )
      throw Error('Use HTTPS, or a local development address.');
  }
  private async request(path: string, body?: unknown) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`${this.url.replace(/\/$/, '')}${path}`, {
        method: body ? 'POST' : 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
      const data: unknown = await response.json();
      if (!response.ok)
        throw Error(
          typeof data === 'object' && data && 'error' in data
            ? String(data.error)
            : 'The backend could not complete this request.',
        );
      return parseSnapshot(data);
    } finally {
      clearTimeout(timeout);
    }
  }
  read() {
    return this.request('/api/state');
  }
  execute(command: Command) {
    return this.request('/api/commands', { command });
  }
  async reset(): Promise<Snapshot> {
    throw Error('Reset is available in offline demo mode only.');
  }
}



